import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import {
  AddBannerObject,
  Banner,
  Banners,
  ChangeBannerObject,
  ChangeBannerResponse,
} from "./banners.interface";
import { map } from "rxjs/operators";
import { Addon, AddonsResponse } from "../addons.interface";

@Injectable({ providedIn: "root" })
export class BannersService {
  constructor(private http: HttpClient) {}

  getBanners = (): Observable<Banners> => {
    return this.http.get<AddonsResponse>("/addons").pipe(
      map((addons) =>
        addons?.BANNER?.map((banner) => ({
          addonId: banner.addonId,
          modifiedAt: banner.modifiedAt,
          isUsed: banner.isUsed,
          usedInSignatures: banner.usedInSignatures ?? [],
          ...banner.data,
        })),
      ),
      map((banners) => ({ banners })),
    );
  };

  getBannerById = (addonId: string): Observable<Banner> => {
    return this.getBanners().pipe(
      map((banners) =>
        banners.banners.find((banner) => banner.addonId === addonId),
      ),
    );
  };

  changeBanner = ({
    addonId,
    ...body
  }: ChangeBannerObject): Observable<Banner> => {
    return this.http
      .put<ChangeBannerResponse>(
        `/addons/${addonId}`,
        this.prepareBannerFormData(body),
      )
      .pipe(
        map(
          (banner) =>
            ({
              addonId,
              ...banner.addon.data,
            }) as Banner,
        ),
      );
  };

  removeBanner = (addonId: string): Observable<unknown> => {
    return this.http.delete(`/addons/${addonId}`);
  };

  addBanner = (payload: AddBannerObject): Observable<Banner> => {
    return this.http
      .post<Addon>("/addons", this.prepareBannerFormData(payload))
      .pipe(
        map(
          (banner) =>
            ({
              addonId: banner.addonId,
              ...banner.data,
            }) as Banner,
        ),
      );
  };

  private prepareBannerFormData = (
    payload: AddBannerObject | Omit<ChangeBannerObject, "addonId">,
  ) => {
    const formData = new FormData();
    formData.append("image", payload.file as Blob);
    formData.append("name", payload.name);
    formData.append("utm", payload.utm);
    formData.append("url", payload.url);
    formData.append("imageWidth", String(payload.imageWidth));
    formData.append("imageHeight", String(payload.imageHeight));
    formData.append("originalImageWidth", String(payload.originalImageWidth));
    formData.append("originalImageHeight", String(payload.originalImageHeight));
    return formData;
  };
}
